import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MediaMatcher} from '@angular/cdk/layout';
import {MatSidenav} from '@angular/material/sidenav';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LoggedInCallback} from '../../../service-auth/cognito.service';
import {RoleChangeListener, StartupService} from '../../../service-ui/startup.service';
import {ServerErrorToastService} from '../../../service-ui/server-error-toast.service';
import {ProjectMappingService} from '../../../service-data/project-mapping.service';
import {BookService} from '../../../service-data/book.service';
import {DraftService} from '../../../service-data/draft.service';
import {LanguagePipe} from '../../../pipe/language.pipe';
import {serverErrorCode, technicalErrorDetail} from '../../../service-data/http-error';
import {MappingNotReadyReason, mappingNotReadyReason, planningWouldHelp} from '../mapping-not-ready';
import {isEndpointNotBuilt} from '../mapping-write-unavailable';
import {orderProposals, trimProposals} from '../mapping-list-trim';
import {
  ComparableCard,
  markComparisons,
  markComparisonsAgainst,
  markSequenceAnomalies
} from '../mapping-compare';
import {
  factsFromDisplay,
  factsFromItem,
  MappingItemFacts,
  recencyNote,
  sideIds
} from '../mapping-item-facts';
import {BodyService} from '../../../service-data/body.service';
import {CoverService} from '../../../service-data/cover.service';
import {DraftBodyService} from '../../../service-data/draft-body.service';
import {DraftCoverService} from '../../../service-data/draft-cover.service';
import {PaperTypeService} from '../../../service-data/paper-type.service';
import {OrganizationService} from '../../../service-data/organization.service';
import {
  buildFacetRows,
  MappingFacetKey,
  MappingFacetRow,
  MappingNearMissRejection,
  nearMissRejection,
  visibleFacetKeys
} from '../mapping-facets';
import {
  PROJECT_MAPPING_SETTLED_PHASES,
  ProjectMappingApplyRequest,
  ProjectMappingApplySelector,
  ProjectMappingDecideRejectedReason,
  ProjectMappingDecision,
  ProjectMappingItemRef,
  ProjectMappingItemDisplay,
  ProjectMappingJoinReason,
  ProjectMappingMember,
  ProjectMappingNearMiss,
  ProjectMappingProposal,
  ProjectMappingRejectedReason,
  ProjectMappingReview,
  ProjectMappingVocabulary
} from '../../../../model/ProjectMapping';

/**
 * One member row, with everything the template needs already resolved.
 *
 * The payload carries ids only. Resolving them into titles is this
 * component's job and the single most important thing it does: a question of
 * the form *"do Book 31884 and Draft 55120 belong together?"* cannot be
 * answered by anybody.
 */
export class MappingMemberView {
  member: ProjectMappingMember;
  title = '';
  author = '';
  /** True while the title is still being fetched, or could not be fetched. */
  unresolved = true;
  /** The label of whatever this member matched, for the reason sentence. */
  joinedToLabel = '';
  /** Local edit state -- NOT sent until the user saves a decision. */
  excluded = false;

  /**
   * The facet grid, in fixed order, already coloured by `joinReason`.
   *
   * Built in the constructor so the card draws its full shape immediately and
   * only the VALUES arrive late. Building it on resolve instead would make
   * every card change height as its item landed.
   */
  facetRows: MappingFacetRow[] = [];

  /** Epoch ms of `lastUpdate`, or 0 when unknown. Drives the recency row. */
  lastUpdateMs = 0;

  /** Set by `markSequenceAnomalies`: this item's id and date disagree. */
  sequenceAnomaly = false;

  /** Mirrors `member.item_ID`, so this satisfies `ComparableCard`. */
  get item_ID(): number { return this.member.item_ID; }

  /** Both needed by `ComparableCard`: the anchor is the payload's, not ours. */
  get itemType(): string { return this.member.itemType; }
  get joinedTo(): ProjectMappingItemRef | null { return this.member.joinedTo; }

  constructor(member: ProjectMappingMember) {
    this.member = member;
    this.excluded = !!member.excluded;
    this.facetRows = buildFacetRows(member.joinReason);
  }

  setFacetValue(key: MappingFacetKey, value: string, numericValue?: number): void {
    for ( const row of this.facetRows ) {
      if ( row.key === key ) {
        row.value = value;
        row.numericValue = numericValue;
        return;
      }
    }
  }

  get isBook(): boolean { return this.member.itemType === 'BOOK'; }

  /** What to show when nothing resolved. Never left blank. */
  get displayTitle(): string {
    return this.title && this.title.length > 0 ? this.title : '';
  }
}

/**
 * Something CONSIDERED for this group and left out.
 *
 * Drawn with the same facet grid as a member, deliberately: "what did it have
 * in common?" and "why was that not enough?" are the two questions a reader
 * asks about a near miss, and the grid answers the first in the same visual
 * language as the cards above it.
 *
 * {@link rejection} is DERIVED, not sent. The server's near-miss shape carries
 * only the code the item connected on - see `nearMissRejection` for exactly
 * how much that permits us to say, and the outbox message asking for a real
 * field.
 */
export class MappingNearMissView {
  ref: ProjectMappingItemRef;
  reason: ProjectMappingJoinReason;
  /** Derived fallback, used only when {@link rejectedReason} is absent. */
  rejection: MappingNearMissRejection;
  /**
   * The server's own answer, when it sends one.
   *
   * Preferred over {@link rejection} wherever present: the derived value is a
   * restatement of the rule, this is what actually happened.
   */
  rejectedReason: ProjectMappingRejectedReason | null = null;
  /** The proposal that took this item instead, for ALREADY_MAPPED. */
  rejectedToName = '';
  label = '';
  author = '';
  unresolved = true;
  facetRows: MappingFacetRow[] = [];

  constructor(nearMiss: ProjectMappingNearMiss) {
    this.ref = { itemType: nearMiss.itemType, item_ID: nearMiss.item_ID };
    this.reason = nearMiss.reason;
    this.rejection = nearMissRejection(nearMiss.reason);
    this.rejectedReason = nearMiss.rejectedReason || null;
    this.rejectedToName = (nearMiss.rejectedTo && nearMiss.rejectedTo.proposedName) || '';
    this.facetRows = buildFacetRows(nearMiss.reason);
  }

  /** Epoch ms of `lastUpdate`, or 0 when unknown. Drives the recency row. */
  lastUpdateMs = 0;

  /** Set by `markSequenceAnomalies`. Near misses are checked too. */
  sequenceAnomaly = false;

  /** Mirrors `ref.item_ID`, so this satisfies `ComparableCard`. */
  get item_ID(): number { return this.ref.item_ID; }

  get itemType(): string { return this.ref.itemType; }

  setFacetValue(key: MappingFacetKey, value: string, numericValue?: number): void {
    for ( const row of this.facetRows ) {
      if ( row.key === key ) {
        row.value = value;
        row.numericValue = numericValue;
        return;
      }
    }
  }

  get isBook(): boolean { return this.ref.itemType === 'BOOK'; }
}

/** A proposal plus the local, unsaved answer being composed for it. */
export class MappingProposalView {
  proposal: ProjectMappingProposal;
  members: MappingMemberView[] = [];
  nearMisses: MappingNearMissView[] = [];
  /** Further near misses the server capped off. Rendered as "and N more". */
  nearMissesTruncated = 0;
  expanded = false;
  /** Local selection; `0` means "leave the server's proposal alone". */
  primaryBook_ID = 0;
  primaryDraft_ID = 0;
  rejected = false;
  saving = false;
  saved = false;

  constructor(proposal: ProjectMappingProposal) {
    this.proposal = proposal;
  }

  get needsDecision(): boolean { return this.proposal.needsDecision; }
  get applied(): boolean { return this.proposal.status === 'APPLIED'; }
  get superseded(): boolean { return this.proposal.status === 'SUPERSEDED'; }
  get answered(): boolean {
    return this.proposal.status === 'ACCEPTED' || this.proposal.status === 'EDITED' || this.proposal.status === 'REJECTED';
  }

  /** The §6a rules that cleared this proposal. Empty when none did. */
  get clearedBy(): string[] {
    return this.proposal.clearedBy || [];
  }

  get cleared(): boolean {
    return this.clearedBy.length > 0;
  }

  /**
   * `PRIMARY_*` reasons, which survive on a CLEARED proposal as information.
   *
   * `decisionReasons` may be non-empty while `needsDecision` is false (server,
   * 2026-10-27): `LOW_CONFIDENCE` is removed when a structural rule proves the
   * grouping, but an ambiguous or stale primary is still a true fact about the
   * dates and deleting it would destroy the only record of it.
   *
   * **Its presence is not a question.** It is rendered as a quiet note and
   * never as an ask - which is the whole reason this getter exists instead of
   * the template reading `decisionReasons` directly.
   */
  get primaryNotes(): string[] {
    return (this.proposal.decisionReasons || [])
      .filter(r => r === 'PRIMARY_AMBIGUOUS' || r === 'PRIMARY_STALE');
  }

  /** Did the planner consider anything for this group and leave it out? */
  get hasAlternatives(): boolean {
    return this.nearMisses.length > 0 || this.nearMissesTruncated > 0;
  }

  get includedMembers(): MappingMemberView[] {
    return this.members.filter(m => !m.excluded);
  }

  get excludedMembers(): MappingMemberView[] {
    return this.members.filter(m => m.excluded);
  }

  /** Has the user changed anything away from what the server proposed? */
  get edited(): boolean {
    if ( this.members.some(m => m.excluded !== !!m.member.excluded) ) {
      return true;
    }
    const proposedBook = this.members.find(m => m.isBook && m.member.isProposedPrimary);
    const proposedDraft = this.members.find(m => !m.isBook && m.member.isProposedPrimary);
    if ( this.primaryBook_ID && proposedBook && this.primaryBook_ID !== proposedBook.member.item_ID ) {
      return true;
    }
    if ( this.primaryDraft_ID && proposedDraft && this.primaryDraft_ID !== proposedDraft.member.item_ID ) {
      return true;
    }
    return false;
  }
}

/**
 * The Project mapping review.
 *
 * ## One component, three roles
 *
 * `network`, `serviceProvider` and `organization` all render THIS component
 * against THE SAME `GET /project/mapping/review/:orgId` payload. There is
 * deliberately no separate operator view. An operator on the phone to a
 * customer has to be looking at exactly what the customer is describing, and
 * two implementations drift precisely in the places where someone is already
 * confused. Role affects only which *actions* are offered -- see
 * `ProjectMappingReview.md` section 2 for the matrix -- never what is shown.
 *
 * ## It must read correctly when it has nothing to say
 *
 * A customer may arrive to find their Projects already built, partly built,
 * or not built at all. All three are normal. "Nothing needs your attention"
 * is a first-class state here, not an empty list.
 *
 * ## Ids are resolved locally, including the ones that are not in the cache
 *
 * `project.books[]` is structurally truncated: `BookService.getItems()` is
 * called nowhere, so Books reach the browser only nested under a Product or a
 * recent PrintNodeOrderItem. This screen is by construction full of the exact
 * population that misses both routes -- unmapped, orphan Books with
 * `work_ID = 0`. Rendering those as bare ids, or omitting them, would ask the
 * customer "do these belong together?" about a list that is not the list.
 *
 * So anything missing from the cache is fetched singly by
 * `getItem(id)`. `GET /book/{id}` is deliberately unfiltered server-side, so
 * it resolves archived Books too, which the collection endpoint will not.
 * When `BookService.getItems()` lands at startup this fallback simply stops
 * firing; nothing here needs to change.
 */
@Component({
  selector: 'app-project-mapping-review',
  templateUrl: './project-mapping-review.component.html',
  styleUrls: ['./project-mapping-review.component.css']
})
export class ProjectMappingReviewComponent implements OnInit, OnDestroy, LoggedInCallback, RoleChangeListener {

  /**
   * Above this many proposals the settled ones are not rendered by default.
   *
   * 50 is well under any figure that hurts and well over the size of an
   * ordinary organization, so the trim is invisible to almost everyone and
   * only engages where it is needed. The largest organization in the estate
   * plans to **1,768** proposals (server, 2026-10-26b §5) - rendered in full
   * that is roughly 25-30k DOM nodes of Material card before the reader has
   * expanded anything, nearly all of it singletons nobody is being asked
   * about.
   */
  private static readonly LARGE_REVIEW = 50;

  /** How many more settled proposals one "show more" reveals. */
  private static readonly REVEAL_BATCH = 50;

  orgId = 0;
  review: ProjectMappingReview = null;
  proposalViews: MappingProposalView[] = [];

  /**
   * The proposals actually rendered. **The template iterates THIS**, never
   * `proposalViews`.
   *
   * Recomputed into a field rather than exposed as a filtering getter on
   * purpose: a getter that filters is re-run on every change-detection pass,
   * so at 1,768 items the cure would have cost more than the disease.
   */
  visibleProposals: MappingProposalView[] = [];

  /** Settled proposals deliberately not rendered. 0 when nothing is trimmed. */
  hiddenProposalCount = 0;

  /**
   * Groups with alternatives that are not currently rendered.
   *
   * They sort directly below the questions, so the ordinary "show more" surfaces
   * them first anyway - but a reader who wants exactly these should not have to
   * reveal an arbitrary batch and hope. Drives its own button.
   */
  hiddenAlternativeCount = 0;

  /** True when the list is being trimmed at all. */
  largeReview = false;

  /**
   * Questions still unanswered. A field, not a getter, for the reason above.
   */
  outstandingCount = 0;

  /** How many settled proposals have been revealed on top of the questions. */
  private revealed = 0;

  /**
   * Proposals that must stay on screen once shown.
   *
   * Without this, answering a question makes its card **vanish mid-click** -
   * `answered` flips, the proposal stops matching "needs a decision", and the
   * thing the reader was just looking at disappears along with any chance to
   * check or change it. Anything that has ever needed attention is pinned for
   * the life of the view.
   */
  private sticky = new Set<MappingProposalView>();
  loading = false;
  /**
   * True when the last load failed. Never silently empty -- see below.
   *
   * Split from {@link loadErrorDetail} because the two have different
   * audiences: this drives what the screen SHOWS (a translated sentence), the
   * detail is a short technical line for support.
   */
  loadFailed = false;

  /** Short technical line. See `technicalErrorDetail`. May be empty. */
  loadErrorDetail = '';

  /**
   * The organization's name, for the header. Empty until resolved, and
   * empty forever if it cannot be.
   *
   * ---------------------------------------------------------------------------
   * WHY (server, 2026-10-28d)
   * ---------------------------------------------------------------------------
   * An operator reported a problem against "the Winder Log organization".
   * There is no such account: Winder Log is a BOOK TITLE, and the organization
   * was 2042, Mitsubishi Polyester Films. The proposal is named after its
   * primary Book, so the Project name and the account name sit on the same
   * screen looking alike, and the header said only "Organization 2042". The
   * server had to correct it, which is a round trip this field removes.
   *
   * §3.3 carries `org_ID` and nothing else a human recognises. The contract's
   * §8.2 records `name` on the review payload as **accepted on 2026-10-22**,
   * but it has not shipped; **when it does, delete this and read it off the
   * payload.** Until then it is joined client-side from
   * `organization/activeForRole` -- the same endpoint, and the same rows, that
   * `ProjectMappingDashboardComponent` already joins `adminEmail` from and
   * that the role picker searches.
   *
   * Rendered VERBATIM, never translated: an organization's name is the
   * customer's own data, which the house rule shows as-is.
   */
  orgName = '';

  /**
   * True once the name lookup has been attempted for {@link orgId}.
   *
   * The lookup returns every organization in scope -- for a `network` operator
   * that is thousands of rows -- so it must happen ONCE per organization, not
   * on every `load()`. `load()` is re-entered by the plan poll, by the run
   * poll and by every `PROJECT_MAPPING_RUN` notification, and re-fetching that
   * list on each would be an absurd way to render one word that cannot change
   * while the screen is open.
   */
  private orgNameRequestedFor = 0;
  applying = false;

  /**
   * True once a write action came back from a route the server has not built.
   *
   * Set by {@link reportWriteFailure}; see `mapping-write-unavailable.ts` for
   * why a 404 with no machine code means "not shipped yet" rather than
   * "failed". While it is set, every action that writes is disabled and the
   * reason is stated once at the top of the screen instead of as a red toast
   * per press.
   *
   * ## Why nothing clears it
   *
   * Deliberately not reset by {@link load}. `load` runs on the plan poll and
   * on a run notification as well as on the Refresh button, so resetting there
   * would re-enable the buttons behind the operator's back; they would press
   * one, get the same 404, and watch the banner reappear. The flag is an
   * observation about the DEPLOYMENT, which cannot change while this screen is
   * open, so the honest lifetime is the lifetime of the component -- navigating
   * away and back re-tests it, which is the right amount of effort for
   * "has the server shipped in the last few seconds?".
   */
  writeUnavailable = false;

  /**
   * Why there is nothing to show, when that is not a failure.
   *
   * `null` means either the payload arrived or the load genuinely failed --
   * {@link loadFailed} tells those two apart. See `mapping-not-ready.ts` for
   * why five server errors are NOT errors as far as this screen is concerned,
   * and why the commonest of them used to make every organization look
   * broken.
   */
  notReady: MappingNotReadyReason | null = null;

  /**
   * True once we have given up resolving ids from this session.
   *
   * ## Why hydration cannot work for an operator
   *
   * Every request carries `selectedrole: <role>-<roleId>`
   * (`StartupService.getHttpOptions`). An operator reviewing organization 412
   * is `network-1`, **not** `organization-412` - so `GET /book/{id}`,
   * `/draft/{id}`, `/body/{id}` and `/cover/{id}` are all evaluated in the
   * operator's own scope and cannot return another organization's rows.
   *
   * The §8.1 single-fetch fallback was designed for the customer looking at
   * their own account, where it does work. On the operator path it is not slow,
   * it is **impossible** - and firing it anyway cost ~120 doomed requests per
   * screen and left every card showing an em-dash with nothing saying why.
   *
   * This is also why the fix cannot be "add an operator hydration endpoint":
   * §8 item 5 settled that all three roles render ONE payload, so anything an
   * operator needs that is not in it cannot be added for operators only. The
   * values have to travel in the mapping itself - see `display` in §3.3.
   */
  hydrationBlocked = false;

  /**
   * Can this session resolve Book/Draft ids at all?
   *
   * Only when the signed-in role IS the organization being reviewed. Operators
   * never can; a customer looking at their own account always can.
   */
  private get canHydrateLocally(): boolean {
    return !this.isOperator && this.orgId > 0 && this.orgId === this.ownOrganizationId();
  }

  /** Pending re-check while a plan is being built. */
  private planPollTimer: any = null;

  /**
   * Re-checks left before this screen stops waiting on its own.
   *
   * A finished plan also arrives as a `PROJECT_MAPPING_RUN` message, which is
   * faster and is the normal path. The poll is the fallback for the session
   * that is not receiving messages -- without it, "a plan is being built"
   * stays on screen forever and the operator has no way to know whether that
   * is true.
   */
  private planPollsLeft = 0;

  private static readonly PLAN_POLL_MS = 4000;
  private static readonly PLAN_POLL_MAX = 30;

  /** Pending re-check while an apply or revert is being carried out. */
  private runPollTimer: any = null;

  /**
   * Re-checks left before this screen stops waiting on a run.
   *
   * Three minutes at three seconds. An apply over a few thousand items is
   * minutes of work, so this is deliberately longer than the plan budget;
   * what it must NOT be is unbounded, because the run continues whether or
   * not anyone is watching and a tab left open would poll for days.
   */
  private runPollsLeft = 0;

  /** True once we have stopped waiting and are asking to be told. */
  runWaitTimedOut = false;

  /**
   * Machine code and server sentence for the last failed WRITE. May be empty.
   *
   * ---------------------------------------------------------------------------
   * WHY (2026-10-28, second report of the same symptom)
   * ---------------------------------------------------------------------------
   * An operator pressed "Create this Project now" and got "The mapping could
   * not be applied. Nothing has been created." The actual cause was
   * `500 PROJECT_MAPPING_APPLY_ENQUEUE_FAILED` -- the apply queue did not
   * exist on that deployment -- and establishing that took a round trip to ask
   * them to open DevTools and read the console.
   *
   * The not-ready panel has shown a labelled "Technical detail" line since it
   * was written, for exactly this reason. The write failures did not, so the
   * one sentence that identifies WHICH failure occurred was reachable only
   * from a console the reader has no reason to open.
   *
   * Shown as secondary, labelled, after the translated sentence -- never in
   * place of it. The server's prose is English in all 21 bundles, so it can
   * only ever be technical detail; the house rule that an end user reads their
   * own language is not relaxed here, it is satisfied by the sentence above it.
   */
  writeErrorDetail = '';

  private static readonly RUN_POLL_MS = 3000;
  private static readonly RUN_POLL_MAX = 60;

  @ViewChild('mainSideNav', { static: true }) mainSideNav: MatSidenav;
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  private onDestroySubject: Subject<boolean> = new Subject();
  /** Ids already requested singly, so a 40-member screen issues one fetch each. */
  /**
   * In-flight fetches, keyed `TYPE:id`, each holding EVERY waiting callback.
   *
   * It used to be a plain `requested[id] = true` flag, and the second caller
   * for an id was dropped on the floor. That is not rare here: an item can be
   * a near miss in one proposal and a member of another, or be named as some
   * other member's `joinedTo` before its own card is reached. Whichever card
   * asked second stayed "Loading this item..." permanently, with every facet
   * row showing a dash, because its callback never ran.
   */
  private pendingFetches: { [key: string]: (() => void)[] } = {};
  /** Keys that have come back, so a later caller resolves immediately. */
  private completedFetches: { [key: string]: boolean } = {};

  constructor(public router: Router,
              public route: ActivatedRoute,
              public startupService: StartupService,
              public projectMappingService: ProjectMappingService,
              public bookService: BookService,
              public draftService: DraftService,
              // A Book's geometry is NOT nested on the Book: `body` and `cover`
              // are reverse-linked from these services by `body_ID`/`cover_ID`
              // (BookService.getLinkData), and nothing else on this screen
              // loads them. Without these four injections the Interior PDF,
              // Cover PDF, Pages and Trim rows can never show a value.
              public bodyService: BodyService,
              public coverService: CoverService,
              public draftBodyService: DraftBodyService,
              public draftCoverService: DraftCoverService,
              // Global reference data, loaded at startup for every role, so an
              // operator can resolve a paper name for another organization's
              // Book where it could never resolve the Book itself.
              public paperTypeService: PaperTypeService,
              // Only to put the organization's NAME on the header. §3.3 carries
              // org_ID and nothing else a human recognises -- see orgName.
              public organizationService: OrganizationService,
              public serverErrorToastService: ServerErrorToastService,
              public changeDetectorRef: ChangeDetectorRef,
              public media: MediaMatcher) {
    this.startupService.addRoleChangeListener(this);
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngOnInit(): void {
    this.startupService.isAuthenticated(this);

    this.route.paramMap.pipe(takeUntil(this.onDestroySubject)).subscribe(params => {
      const routeOrgId = Number(params.get('orgId'));
      this.orgId = routeOrgId > 0 ? routeOrgId : this.ownOrganizationId();
      this.load();
    });

    // Run-level progress only. The per-item BOOK/DRAFT burst an apply
    // publishes -- roughly one message per item, so thousands of them -- is
    // deliberately ignored: counting it would mean thousands of change
    // detection passes to render one progress number.
    this.projectMappingService.runUpdates.pipe(takeUntil(this.onDestroySubject)).subscribe(run => {
      // A run for THIS organization, while we are sitting on "not planned" or
      // "being built". There is no `review` to match a runId against yet, so
      // the organization id is the only thing that can connect the message to
      // the screen -- and without this the page waits for its own poll when
      // the answer has already arrived.
      if ( run && run.org_ID && Number(run.org_ID) === Number(this.orgId) && !this.review ) {
        if ( run.phase === 'PLANNED' || run.phase === 'FAILED' ) {
          this.stopPlanPoll();
          this.load();
        }
        return;
      }
      if ( this.review && run && run.runId === this.review.runId ) {
        this.review.phase = run.phase;
        if ( PROJECT_MAPPING_SETTLED_PHASES.indexOf(run.phase) >= 0 ) {
          // The fast path beat the poll. Stop it, or it keeps asking about a
          // run that has already settled and reloads the screen underneath
          // the reader a second time.
          this.stopRunPoll();
          this.applying = false;
          this.load();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.stopPlanPoll();
    this.stopRunPoll();
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  /**
   * Re-check while a plan is being built.
   *
   * Deliberately bounded. An unbounded poll against a run that will never
   * finish is a request every four seconds for as long as the tab is open,
   * and it leaves "a plan is being built" on screen as a permanent and
   * untrue statement. When the budget runs out the panel offers a manual
   * re-check instead, which is an honest admission that we have stopped
   * waiting.
   */
  private startPlanPoll(): void {
    if ( this.planPollTimer ) {
      return;
    }
    // Out of budget: stop, and let `planWaitTimedOut` offer the manual
    // re-check. This used to REFILL the budget here instead of reading it,
    // which made the poll unbounded and that getter permanently false.
    if ( this.planPollsLeft <= 0 ) {
      return;
    }
    this.planPollTimer = setTimeout(() => {
      this.planPollTimer = null;
      this.planPollsLeft--;
      this.load();
    }, ProjectMappingReviewComponent.PLAN_POLL_MS);
  }

  private stopPlanPoll(): void {
    if ( this.planPollTimer ) {
      clearTimeout(this.planPollTimer);
      this.planPollTimer = null;
    }
    this.planPollsLeft = 0;
  }

  /** True when the screen has given up waiting and is asking to be told. */
  get planWaitTimedOut(): boolean {
    return this.notReady === 'PLANNING' && !this.planPollTimer && this.planPollsLeft <= 0;
  }

  /** Start waiting again after the plan budget ran out. */
  retryPlanWait(): void {
    this.planPollsLeft = ProjectMappingReviewComponent.PLAN_POLL_MAX;
    this.load();
  }

  /** True when an operator pressing Plan is the move that unblocks this. */
  get planWouldHelp(): boolean {
    return this.canPlan && planningWouldHelp(this.notReady);
  }

  isLoggedIn(message: string, isLoggedIn: boolean): void {
    if ( !isLoggedIn ) {
      this.router.navigate(['/home/login']);
    }
  }

  roleChanged(role: string, roleId: string): void {
    this.orgId = this.ownOrganizationId();
    // A different role sees a different set of organizations, so a name
    // resolved under the previous one must not be carried across a scope
    // boundary -- the same reasoning as the dashboard's email join.
    this.orgName = '';
    this.orgNameRequestedFor = 0;
    this.load();
  }

  /**
   * Put the organization's name on the header. Best-effort, operator-only.
   *
   * Operator-only because a customer is looking at their own account and does
   * not need to be told which one it is; the header block is gated on
   * `isOperator` for the same reason.
   *
   * Deliberately fired AFTER the review has rendered and deliberately
   * failure-tolerant: nothing the operator asked for depends on it, the id is
   * already on screen, and a toast about a decorative field would be noise.
   * A failure is recorded once, in the console, and not retried -- see
   * {@link orgNameRequestedFor}.
   */
  private loadOrganizationName(): void {
    if ( !this.isOperator || !this.orgId || this.orgNameRequestedFor === this.orgId ) {
      return;
    }
    const role = this.startupService.selectedRole;
    const roleId = this.startupService.selectedRoleId;
    if ( !role || !roleId ) {
      return;
    }
    // Claimed BEFORE the request, not after: load() can be re-entered by a
    // poll while this is still in flight, and marking it on success would
    // issue the whole list again for every re-entry until the first returned.
    this.orgNameRequestedFor = this.orgId;
    const wanted = this.orgId;
    this.organizationService.getActiveOrganizationsForRole(role, roleId)
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(
        list => {
          const match = ( list || [] ).find(org => org && Number(org.ID) === wanted);
          // Only if the screen is still on the organization we asked about.
          if ( match && match.name && this.orgId === wanted ) {
            this.orgName = match.name;
          }
        },
        error => {
          console.error('ProjectMappingReviewComponent: organization name unavailable', error);
        }
      );
  }

  private ownOrganizationId(): number {
    return this.startupService.selectedRole === 'organization' ? Number(this.startupService.selectedRoleId) : 0;
  }

  // --- role capabilities ----------------------------------------------------

  /** `network` and `serviceProvider`. Contract section 2. */
  get isOperator(): boolean {
    return this.projectMappingService.isOperator();
  }

  /**
   * Revert is operator-only on purpose. An organization that dislikes a result
   * uses the ordinary move and merge affordances it already understands;
   * "undo an entire backfill run" is not a concept a customer should hold.
   */
  get canRevert(): boolean {
    return this.isOperator && this.review && this.review.counts && this.review.counts.applied > 0;
  }

  get canPlan(): boolean {
    return this.isOperator;
  }

  // --- loading --------------------------------------------------------------

  load(): void {
    if ( !this.orgId ) {
      return;
    }
    this.loading = true;
    this.loadFailed = false;
    this.loadErrorDetail = '';
    this.hydrationBlocked = false;
    this.projectMappingService.getReview(this.orgId).subscribe(
      review => {
        this.loading = false;
        this.review = review;
        this.notReady = null;
        this.stopPlanPoll();
        this.buildViews();
        this.loadOrganizationName();
      },
      error => {
        this.loading = false;
        this.review = null;
        this.proposalViews = [];
        this.loadErrorDetail = technicalErrorDetail(error);

        // Five of the errors this endpoint returns are not failures: nobody
        // has planned this organization, a plan is being built, the last run
        // failed, the plan body has aged out, or this role may not look. Each
        // has its own next action, and rendering them as "could not be
        // loaded" reported an outage for the ordinary beginning of the
        // process -- which, before any organization had been planned, was
        // every organization on the dashboard.
        this.notReady = mappingNotReadyReason(serverErrorCode(error));
        if ( this.notReady ) {
          this.loadFailed = false;
          if ( this.notReady === 'PLANNING' ) {
            // Budget is granted here, where a PLANNING answer is actually
            // observed, rather than inside startPlanPoll where re-arming the
            // timer kept refilling it.
            if ( this.planPollsLeft <= 0 && !this.planWaitTimedOut ) {
              this.planPollsLeft = ProjectMappingReviewComponent.PLAN_POLL_MAX;
            }
            this.startPlanPoll();
          } else {
            this.stopPlanPoll();
          }
          return;
        }

        // A genuine failure must never render as "nothing needs your
        // attention". That is the one wrong answer this screen must not give,
        // because it closes the question instead of raising it.
        this.stopPlanPoll();
        this.loadFailed = true;
        console.error('ProjectMappingReviewComponent: review load failed', error);
      }
    );
  }

  private buildViews(): void {
    this.proposalViews = [];
    // A new payload means new view objects, so the old pins and reveal count
    // refer to nothing. Not clearing them would leak one run's state into the
    // next re-plan.
    this.visibleProposals = [];
    this.sticky = new Set<MappingProposalView>();
    this.revealed = 0;
    this.heldProposalIds = new Set<number>();
    if ( !this.review || !this.review.proposals ) {
      this.refreshVisible();
      return;
    }
    for ( const proposal of this.review.proposals ) {
      const view = new MappingProposalView(proposal);
      // PAYLOAD ORDER, deliberately unsorted.
      //
      // Planner 1.2.0 emits members in reading order - Books by ID descending,
      // then Drafts by lastUpdate descending - and sets each member's
      // `joinedTo` to the item before it OF ITS OWN KIND. The array order and
      // the explanation now agree, so rendering it as it stands is what makes
      // a card's stated reason describe the card beside it.
      //
      // This used to re-sort by `joinReasonRank`, which broke precisely that:
      // re-ordering moved an unrelated item between a member and the sibling
      // it was compared against, so the left-to-right comparison chain in
      // `markComparisons` measured against the wrong card. That is what marked
      // a Title row "differs" against a title it matches character for
      // character (server, 2026-10-26d §3).
      const members = (proposal.members || []);
      for ( const member of members ) {
        const memberView = new MappingMemberView(member);
        view.members.push(memberView);
        if ( member.isProposedPrimary ) {
          if ( member.itemType === 'BOOK' ) {
            view.primaryBook_ID = member.item_ID;
          } else {
            view.primaryDraft_ID = member.item_ID;
          }
        }
      }
      for ( const nearMiss of (proposal.nearMisses || []) ) {
        view.nearMisses.push(new MappingNearMissView(nearMiss));
      }
      view.nearMissesTruncated = proposal.nearMissesTruncated || 0;
      this.applyFacetVisibility(view);
      // Anything that still needs an answer opens; everything else stays shut.
      // An organization in AWAITING_CUSTOMER should see a short list of
      // questions, not its whole catalogue.
      view.expanded = proposal.needsDecision;
      this.proposalViews.push(view);
    }
    this.orderProposals();
    this.refreshVisible();
    this.resolveLabels();
  }

  /**
   * Secondary ordering, applied once per load.
   *
   * Logic and reasoning live in `mapping-list-trim.ts` so they can be tested;
   * see `orderProposals` there. Called once, from `buildViews` - re-sorting on
   * every refresh would make cards jump as the reader answers them.
   */
  private orderProposals(): void {
    this.proposalViews = orderProposals<MappingProposalView>(
      this.proposalViews,
      v => v.needsDecision && !v.answered,
      v => v.hasAlternatives);
  }

  /**
   * Drop the rows this proposal has no use for.
   *
   * **Pruned per proposal, never per card.** Every card in a group has to draw
   * the same rows in the same positions, or the cross-card comparison the grid
   * exists for is destroyed - so the decision is taken once, from every reason
   * in the group, and applied to members and near misses alike.
   *
   * Near-miss reasons are included in the decision: an item rejected on an
   * ISBN should be able to show the ISBN it was rejected on.
   */
  private applyFacetVisibility(view: MappingProposalView): void {
    const reasons: ProjectMappingJoinReason[] = [];
    for ( const member of (view.proposal.members || []) ) {
      reasons.push(member.joinReason);
    }
    for ( const nearMiss of (view.proposal.nearMisses || []) ) {
      reasons.push(nearMiss.reason);
    }
    const allowed = visibleFacetKeys(reasons);
    const prune = (rows: MappingFacetRow[]) => rows.filter(r => allowed.indexOf(r.key) >= 0);
    for ( const member of view.members ) {
      member.facetRows = prune(member.facetRows);
    }
    for ( const nearMiss of view.nearMisses ) {
      nearMiss.facetRows = prune(nearMiss.facetRows);
    }
  }

  /** Resolve every id on screen into a title, fetching what the caches lack. */
  private resolveLabels(): void {
    for ( const view of this.proposalViews ) {
      for ( const memberView of view.members ) {
        this.resolveMember(memberView);
      }
      for ( let i = 0; i < view.nearMisses.length; i++ ) {
        const source = (view.proposal.nearMisses || [])[i];
        this.resolveNearMiss(view.nearMisses[i], source && source.display);
      }
      this.applyRecency(view);
    }
  }

  /**
   * Fill the `UPDATED` row: the date, and how far it is from the newest item.
   *
   * ## The baseline is the newest MEMBER, not the newest card
   *
   * Near misses are measured against the same baseline but never set it. The
   * question is "which version of THIS GROUP is current?", and letting a
   * rejected item define the zero point would answer a different one - a near
   * miss that happens to be newer would push every real member to "older" and
   * make the group look stale when it is not.
   *
   * A near miss newer than the whole group still reads correctly, as
   * `DAYS_NEWER`, which is genuinely useful: it is the strongest argument that
   * the exclusion is wrong.
   *
   * ## Called again on every late resolution
   *
   * Items arrive one fetch at a time, so the newest is not known until the
   * last one lands. This recomputes the whole proposal each time rather than
   * patching one card, which is cheap at these sizes and cannot drift.
   * Anything with no usable date is skipped entirely and keeps an empty row.
   */
  private applyRecency(view: MappingProposalView): void {
    let newest = 0;
    for ( const memberView of view.members ) {
      if ( memberView.lastUpdateMs > newest ) {
        newest = memberView.lastUpdateMs;
      }
    }
    const note = (card: { lastUpdateMs: number, facetRows: MappingFacetRow[] }) => {
      const row = card.facetRows.filter(r => r.key === 'UPDATED')[0];
      if ( !row ) {
        return;
      }
      row.valueDate = card.lastUpdateMs || undefined;
      row.detail = '';
      row.detailKind = undefined;
      const note = recencyNote(card.lastUpdateMs, newest);
      if ( !note ) {
        return;
      }
      row.detailKind = note.kind;
      row.detail = note.days ? LanguagePipe.localizeNumber(note.days) : '';
    };
    for ( const memberView of view.members ) {
      note(memberView);
    }
    for ( const nearMiss of view.nearMisses ) {
      note(nearMiss);
    }

    // Card-to-card differences, and whether id order agrees with edit order.
    // Both run here because both depend on values that arrive late, and this
    // is already the function every late arrival calls back into.
    markComparisons(view.members as ComparableCard[]);
    markSequenceAnomalies(view.members as ComparableCard[]);

    // Near misses are compared against the version the group is KEEPING, not
    // against each other. A rejected candidate is a single item held up
    // against the group, so "how does it differ from what we are keeping?" is
    // the only comparison that answers anything.
    //
    // They used to be chained to each other, which drew the first one with no
    // marks at all - a proposal with a single near miss showed an entirely
    // uncoloured card - and measured the second against something that is not
    // even in the group.
    for ( const nearMiss of view.nearMisses ) {
      const reference = this.referenceMemberFor(view, nearMiss.isBook);
      markComparisonsAgainst(reference as ComparableCard, [nearMiss as ComparableCard]);
    }
  }

  /**
   * What a near miss should be measured against.
   *
   * The primary of its own kind first - comparing a rejected Draft with the
   * Draft being kept is a like-for-like question, where comparing it with a
   * Book drags in differences that are only Book-versus-Draft. Then any
   * primary, then the first member, so a card is never left with no reference
   * and therefore no colour at all.
   */
  private referenceMemberFor(view: MappingProposalView, isBook: boolean): MappingMemberView {
    const sameKind = view.members.filter(m => m.isBook === isBook);
    const samePrimary = sameKind.filter(m => this.isPrimary(view, m))[0];
    if ( samePrimary ) {
      return samePrimary;
    }
    const anyPrimary = view.members.filter(m => this.isPrimary(view, m))[0];
    if ( anyPrimary ) {
      return anyPrimary;
    }
    return sameKind[0] || view.members[0] || null;
  }

  /** The proposal a card belongs to, so a late fetch can refresh its deltas. */
  private viewContaining(card: any): MappingProposalView {
    for ( const view of this.proposalViews ) {
      if ( view.members.indexOf(card) >= 0 || view.nearMisses.indexOf(card) >= 0 ) {
        return view;
      }
    }
    return null;
  }

  private resolveMember(memberView: MappingMemberView): void {
    // Inline properties win, and skip the fetch entirely.
    if ( this.applyDisplay(memberView, memberView.member.display) ) {
      memberView.title = memberView.member.display.title || '';
      memberView.author = memberView.member.display.author || '';
      memberView.unresolved = false;
    }
    const apply = () => {
      const item = this.lookupItem(memberView.member);
      if ( item ) {
        memberView.title = item.title || '';
        memberView.author = item.author || '';
        memberView.unresolved = false;
        this.applyFacetValues(memberView, item, memberView.isBook, apply);
        // This card's date may be the newest in its group, which changes every
        // other card's delta - so the whole proposal is recomputed, not just
        // this row.
        const owner = this.viewContaining(memberView);
        if ( owner ) {
          this.applyRecency(owner);
        }
      }
      if ( memberView.member.joinedTo ) {
        const target = this.lookupLabel(memberView.member.joinedTo);
        if ( target ) {
          memberView.joinedToLabel = target.title;
        }
      }
    };
    if ( memberView.unresolved ) {
      apply();
    }
    if ( memberView.unresolved ) {
      this.fetch(memberView.member, apply);
    }
    // The sibling's name is needed for the reason sentence even when this
    // member resolved inline, so this is outside the guard above.
    if ( memberView.member.joinedTo && !memberView.joinedToLabel ) {
      this.resolveJoinedToLabel(memberView);
    }
  }

  /**
   * Name whatever this member matched, for the reason sentence.
   *
   * Prefers the sibling's own inline block over a fetch: `joinedTo` always
   * points at another member of the SAME proposal, so when the server sends
   * `display` the name is already in the payload and no request is needed.
   */
  private resolveJoinedToLabel(memberView: MappingMemberView): void {
    const target = memberView.member.joinedTo;
    const sibling = this.findMemberDisplay(target);
    if ( sibling && sibling.title ) {
      memberView.joinedToLabel = sibling.title;
      return;
    }
    const resolved = this.lookupLabel(target);
    if ( resolved ) {
      memberView.joinedToLabel = resolved.title;
      return;
    }
    this.fetch(target, () => {
      const retry = this.lookupLabel(target);
      if ( retry ) {
        memberView.joinedToLabel = retry.title;
      }
    });
  }

  /** The inline block for an item already present in the loaded payload. */
  private findMemberDisplay(ref: ProjectMappingItemRef): ProjectMappingItemDisplay {
    if ( !ref || !this.review || !this.review.proposals ) {
      return null;
    }
    for ( const proposal of this.review.proposals ) {
      for ( const member of (proposal.members || []) ) {
        if ( member.item_ID === ref.item_ID && member.itemType === ref.itemType && member.display ) {
          return member.display;
        }
      }
    }
    return null;
  }

  private resolveNearMiss(nearMiss: MappingNearMissView, display?: ProjectMappingItemDisplay): void {
    if ( this.applyDisplay(nearMiss, display) ) {
      nearMiss.label = display.title || '';
      nearMiss.author = display.author || '';
      nearMiss.unresolved = false;
      return;
    }
    const apply = () => {
      const item = this.lookupItem(nearMiss.ref);
      if ( !item ) {
        return;
      }
      nearMiss.label = item.title || '';
      nearMiss.author = item.author || '';
      nearMiss.unresolved = false;
      this.applyFacetValues(nearMiss, item, nearMiss.isBook, apply);
      const owner = this.viewContaining(nearMiss);
      if ( owner ) {
        this.applyRecency(owner);
      }
    };
    apply();
    if ( nearMiss.unresolved ) {
      this.fetch(nearMiss.ref, apply);
    }
  }

  /**
   * Fill the facet grid straight from the payload, if the server sent it.
   *
   * Returns false when there is nothing inline, which is the signal to fall
   * back to the cache-and-fetch path. That fallback is not legacy: it is what
   * runs against any server that has not shipped `display` yet, and it has to
   * keep working unchanged.
   *
   * Presence is tested on the OBJECT, not on `title`. A genuinely untitled
   * Draft has `title: ""`, and treating that as "not sent" would fire N+1
   * fetches for exactly the items the block was added to cover.
   */
  private applyDisplay(target: { setFacetValue: (k: MappingFacetKey, v: string, n?: number) => void, lastUpdateMs?: number },
                       display?: ProjectMappingItemDisplay): boolean {
    if ( !display ) {
      return false;
    }
    this.writeFacts(target, factsFromDisplay(display));
    return true;
  }

  /**
   * Write one set of facts onto a card's grid.
   *
   * The single place either source lands, so the inline-`display` path and the
   * cache path cannot drift -- they were two near-copies of the same eight
   * assignments, which is how the inline path silently kept working while the
   * cache path rendered dashes.
   *
   * Numbers are localized HERE and nowhere earlier: `mapping-item-facts` is
   * Angular-free so it can be smoke-tested, and `LanguagePipe` is not.
   */
  private writeFacts(target: { setFacetValue: (k: MappingFacetKey, v: string, n?: number) => void, lastUpdateMs?: number },
                     facts: MappingItemFacts): void {
    const num = (v: number) => (v ? LanguagePipe.localizeNumber(v) : '');
    target.setFacetValue('TITLE', facts.title);
    target.setFacetValue('AUTHOR', facts.author);
    // The raw id goes alongside the localized text: resource ids are issued in
    // ascending order, so the comparison can say "newer" rather than
    // "differs" - but only from the number, never from the display string.
    target.setFacetValue('BODY', num(facts.bodyResource_ID), facts.bodyResource_ID);
    target.setFacetValue('COVER', num(facts.coverResource_ID), facts.coverResource_ID);
    target.setFacetValue('PAGES', num(facts.numPages), facts.numPages);
    target.setFacetValue('TRIM', this.formatTrimPoints(facts.trimWidth, facts.trimHeight));
    target.setFacetValue('BINDING', this.codeText(facts.bindingType));
    target.setFacetValue('PAPER', this.paperTypeName(facts.paperType_ID));
    target.setFacetValue('LAMINATION', this.codeText(facts.laminateType));
    target.setFacetValue('IDENTIFIER', facts.isbn);
    target.setFacetValue('PROJECT', num(facts.work_ID));
    target.lastUpdateMs = facts.lastUpdateMs;
  }

  /**
   * A raw enum code (`PERFECT`, `GLOSS`) as display text.
   *
   * These go through the runtime map rather than an `i18n` attribute because
   * the value is data, not markup - the template cannot switch over a set the
   * server may extend. Every current code is already in all 21 bundles; an
   * unknown one renders as itself, which is visible but not a crash.
   */
  private codeText(code: string): string {
    return code ? LanguagePipe.get(code, 'compressSpaces') : '';
  }

  /**
   * Interior paper name.
   *
   * Falls back to the raw id rather than blank: `P:12` tells an operator
   * something is there and lets them quote it, where an empty row reads as
   * "no paper recorded".
   */
  private paperTypeName(paperType_ID: number): string {
    if ( !paperType_ID ) {
      return '';
    }
    const paperType = this.paperTypeService.objects.find(p => p.ID === paperType_ID);
    return paperType
      ? LanguagePipe.get(paperType.name, 'compressSpaces')
      : 'P:' + LanguagePipe.localizeNumber(paperType_ID);
  }

  /**
   * Fill the facet grid from the resolved Book or Draft.
   *
   * The two sides name the same things differently - `body`/`cover` on a Book,
   * `draftBody`/`draftCover` on a Draft - which is the whole reason this is
   * one function taking a flag rather than two call sites reaching into the
   * object. Legacy naming; see AGENTS.md.
   *
   * Numbers go through `localizeNumber` because a raw `{{ value }}` renders
   * Western digits in every locale. The IDENTIFIER row is the deliberate
   * exception and stays Western: an ISBN is a code to be typed into another
   * system, not a quantity.
   */
  private applyFacetValues(target: { setFacetValue: (k: MappingFacetKey, v: string, n?: number) => void, lastUpdateMs?: number },
                           item: any, isBook: boolean, reapply?: () => void): void {
    // Fetched separately when nothing holds them; `reapply` re-runs this once
    // they land, so the geometry rows fill in rather than staying dashes.
    const refresh = reapply || (() => undefined);
    const body = this.sideFor(item, isBook, false, refresh);
    const cover = this.sideFor(item, isBook, true, refresh);
    this.writeFacts(target, factsFromItem(item, body, cover));
  }

  /**
   * Points to inches.
   *
   * The server sends geometry in points because that is how Body/DraftBody
   * store it and because it has no locale; converting and localizing the
   * digits is the client's job either way, so both sources land here.
   */
  private formatTrimPoints(width: number, height: number): string {
    if ( !width || !height ) {
      return '';
    }
    const inches = (pts: number) => LanguagePipe.localizeNumber(Math.round((pts / 72) * 100) / 100);
    return inches(width) + 'x' + inches(height);
  }

  private lookupItem(ref: ProjectMappingItemRef): any {
    return ref.itemType === 'BOOK'
      ? this.bookService.getLocalItem(ref.item_ID)
      : this.draftService.getLocalItem(ref.item_ID);
  }

  private lookupLabel(ref: ProjectMappingItemRef): { title: string, author: string } {
    const item: any = this.lookupItem(ref);
    if ( !item ) {
      return null;
    }
    return { title: item.title || '', author: item.author || '' };
  }

  private fetch(ref: ProjectMappingItemRef, done: () => void): void {
    if ( !this.canHydrateLocally ) {
      // Would be answered in the wrong organization's scope. Say so once and
      // send nothing - see `hydrationBlocked`.
      this.hydrationBlocked = true;
      return;
    }
    // Typed as `Observable<any>` deliberately: a union of `Observable<Book>`
    // and `Observable<Draft>` has no callable `pipe` signature, because
    // TypeScript intersects the two generic overload sets and gets nothing.
    this.fetchOnce('' + ref.itemType + ':' + ref.item_ID, done, () => (ref.itemType === 'BOOK'
      ? this.bookService.getItem(ref.item_ID)
      : this.draftService.getItem(ref.item_ID)) as Observable<any>);
  }

  /**
   * Fetch `key` at most once, and tell EVERYONE who asked.
   *
   * The two rules that matter, both learned the hard way:
   *
   * - **Every caller's callback is kept**, not just the first. See
   *   {@link pendingFetches}.
   * - **A failure still completes the key.** These getters funnel errors
   *   through `handleError`, which returns `of(undefined)` -- so failure
   *   arrives at `next`, not `error`. Either way the key must be marked done,
   *   or a permanently-404ing id is re-requested on every change-detection
   *   pass for as long as the screen is open.
   */
  private fetchOnce(key: string, done: () => void, request: () => Observable<any>): void {
    if ( this.completedFetches[key] ) {
      done();
      return;
    }
    if ( this.pendingFetches[key] ) {
      this.pendingFetches[key].push(done);
      return;
    }
    this.pendingFetches[key] = [done];
    const settle = () => {
      this.completedFetches[key] = true;
      const waiting = this.pendingFetches[key] || [];
      delete this.pendingFetches[key];
      for ( const callback of waiting ) {
        callback();
      }
    };
    request().pipe(takeUntil(this.onDestroySubject)).subscribe(settle, settle);
  }

  /**
   * The Body/Cover row for an item, fetching it if nothing holds it yet.
   *
   * Three places can have it, in cost order: the reverse link on the item, the
   * side service's own cache, or the server. The link is usually empty on this
   * screen -- these services are not loaded at startup and this surface is
   * full of orphan Books, which is precisely the population nothing else pulls
   * in.
   */
  private sideFor(item: any, isBook: boolean, wantCover: boolean, done: () => void): any {
    const linked = isBook
      ? (wantCover ? item.cover : item.body)
      : (wantCover ? item.draftCover : item.draftBody);
    if ( linked ) {
      return linked;
    }
    const ids = sideIds(item);
    const id = wantCover ? ids.cover_ID : ids.body_ID;
    if ( !id ) {
      return null;
    }
    const service: any = isBook
      ? (wantCover ? this.coverService : this.bodyService)
      : (wantCover ? this.draftCoverService : this.draftBodyService);
    const cached = service.getLocalItem(id);
    if ( cached ) {
      return cached;
    }
    const key = (isBook ? 'B' : 'D') + (wantCover ? 'COVER' : 'BODY') + ':' + id;
    // Already asked, and the cache still does not have it. Return empty and do
    // NOT ask again.
    //
    // This guard is load-bearing, not defensive. `done` re-runs the card's
    // apply(), which calls back into this function -- so handing a completed
    // key to `fetchOnce` (which invokes `done` synchronously for anything
    // already settled) would recurse until the stack blew. A side row that
    // genuinely cannot be fetched leaves its rows blank, permanently and
    // cheaply.
    if ( this.completedFetches[key] ) {
      return null;
    }
    if ( !this.canHydrateLocally ) {
      this.hydrationBlocked = true;
      return null;
    }
    this.fetchOnce(key, done, () => service.getItem(id) as Observable<any>);
    return null;
  }

  // --- facet legend ---------------------------------------------------------

  /** Collapsed by default - it is a key, not a thing to read every visit. */
  legendOpen = false;

  toggleLegend(): void {
    this.legendOpen = !this.legendOpen;
  }

  // --- decisions ------------------------------------------------------------

  toggleExpanded(view: MappingProposalView): void {
    view.expanded = !view.expanded;
  }

  toggleExcluded(view: MappingProposalView, memberView: MappingMemberView): void {
    memberView.excluded = !memberView.excluded;
    // An excluded member cannot also be the primary; silently leaving it as
    // primary would send a decision the server has to reject.
    if ( memberView.excluded ) {
      if ( memberView.isBook && view.primaryBook_ID === memberView.member.item_ID ) {
        view.primaryBook_ID = 0;
      }
      if ( !memberView.isBook && view.primaryDraft_ID === memberView.member.item_ID ) {
        view.primaryDraft_ID = 0;
      }
    }
    view.saved = false;
  }

  setPrimary(view: MappingProposalView, memberView: MappingMemberView): void {
    if ( memberView.excluded ) {
      return;
    }
    if ( memberView.isBook ) {
      view.primaryBook_ID = memberView.member.item_ID;
    } else {
      view.primaryDraft_ID = memberView.member.item_ID;
    }
    view.saved = false;
  }

  isPrimary(view: MappingProposalView, memberView: MappingMemberView): boolean {
    return memberView.isBook
      ? view.primaryBook_ID === memberView.member.item_ID
      : view.primaryDraft_ID === memberView.member.item_ID;
  }

  /** "Yes, these belong together." */
  accept(view: MappingProposalView): void {
    view.rejected = false;
    this.sendDecision(view, view.edited ? 'EDITED' : 'ACCEPTED');
  }

  /**
   * "No, keep these separate."
   *
   * `REJECTED` does not mean "leave them unmapped": on apply the members
   * become singleton Projects. Worth knowing, because the button has to be
   * safe to press -- nothing is lost by saying no.
   */
  reject(view: MappingProposalView): void {
    view.rejected = true;
    this.sendDecision(view, 'REJECTED');
  }

  /**
   * Report a failed WRITE, distinguishing "not built yet" from "went wrong".
   *
   * Every write on this screen funnels through here rather than calling the
   * toast service directly, so that the one unbuilt-route sentence cannot
   * drift between decide, apply and revert -- an operator who sees three
   * different explanations for the same missing deployment learns nothing
   * from any of them.
   *
   * `reportLocalizedError` in both branches: this screen is shown to
   * CUSTOMERS as well as operators, so an English-only sentence is not
   * acceptable here. The server's own prose is logged either way.
   */
  private reportWriteFailure(operation: string, error: any, fallback: string): void {
    // The code is what identifies the failure; the prose is what explains it.
    // Both, because a bare code is unreadable and bare prose is ambiguous --
    // "could not be queued" and "no plan body" are different outages that
    // produce similar sentences.
    const code = serverErrorCode(error);
    const detail = technicalErrorDetail(error);
    this.writeErrorDetail = ( code && detail ) ? `${code} - ${detail}` : ( code || detail );

    if ( isEndpointNotBuilt(error) ) {
      this.writeUnavailable = true;
      this.serverErrorToastService.reportLocalizedError(operation, error,
        LanguagePipe.get('This is not available yet. Nothing has been changed.'));
      return;
    }
    this.serverErrorToastService.reportLocalizedError(operation, error, fallback);
  }

  /**
   * Record an answer.
   *
   * `onSaved` runs only after the server has accepted it, which is what lets
   * "create this Project now" persist a pending exclusion before applying -
   * see {@link createOne}. It is deliberately not called on failure: the
   * decision is the thing being relied on, so if it did not land, nothing
   * downstream should proceed.
   */
  private sendDecision(view: MappingProposalView, decision: 'ACCEPTED' | 'REJECTED' | 'EDITED',
                       onSaved?: () => void): void {
    if ( !this.review || this.writeUnavailable ) {
      return;
    }
    const payload: ProjectMappingDecision = {
      proposalId: view.proposal.proposalId,
      decision: decision
    };
    if ( decision !== 'REJECTED' ) {
      const excluded = view.excludedMembers.map(m => ({ itemType: m.member.itemType, item_ID: m.member.item_ID }));
      if ( excluded.length > 0 ) {
        payload.excludedMembers = excluded;
      }
      if ( view.primaryBook_ID ) {
        payload.primaryBook_ID = view.primaryBook_ID;
      }
      if ( view.primaryDraft_ID ) {
        payload.primaryDraft_ID = view.primaryDraft_ID;
      }
    }
    view.saving = true;
    this.writeErrorDetail = '';
    const proposalId = view.proposal.proposalId;
    this.projectMappingService.decide({ runId: this.review.runId, decisions: [payload] }).subscribe(
      response => {
        view.saving = false;

        // 🚨 A 200 does NOT mean this answer landed. 🚨
        //
        // §3.4 records the entries it understands and returns the rest in
        // `rejected[]`, on purpose: one stale id must not discard a
        // customer's other answers. So the body has to be read. Showing the
        // saved tick on an unexamined 200 is how an answer that was never
        // stored looks stored.
        const refused = ( response && Array.isArray(response.rejected) ? response.rejected : [] )
          .find(r => r && Number(r.proposalId) === proposalId);
        if ( refused ) {
          view.saved = false;
          this.reportDecisionRefused(refused.reason);
          return;
        }

        // 🚨 And it does not mean the decision we SENT is the one stored. 🚨
        //
        // `EDITED` is implied whenever the membership or a primary differs
        // from the proposal, even when we said `ACCEPTED` -- the stored
        // record has to describe what will actually be built. Read it back
        // rather than assuming ours survived; this badge is exactly what a
        // reader checks to see whether their exclusion took.
        const recorded = ( response && Array.isArray(response.recorded) ? response.recorded : [] )
          .find(r => r && Number(r.proposalId) === proposalId);
        view.saved = true;
        view.proposal.status = ( recorded && recorded.decision ) ? recorded.decision : decision;
        view.expanded = false;
        // The outstanding count just changed. The card itself stays put - it
        // is pinned by `sticky`.
        this.refreshVisible();
        if ( onSaved ) {
          onSaved();
        }
      },
      error => {
        view.saving = false;
        view.saved = false;
        this.reportWriteFailure('projectMapping.decide', error,
          LanguagePipe.get('Your answer could not be saved. Nothing has been changed.'));
      }
    );
  }

  /**
   * One entry of a decide request came back refused.
   *
   * The two reasons are not the same kind of event and must not share a
   * sentence:
   *
   * - `UNKNOWN_PROPOSAL` means the plan was regenerated underneath the
   *   reader, so the group they just answered no longer exists. Nothing is
   *   broken and refreshing fixes it. The page is deliberately NOT reloaded
   *   automatically -- a reload rebuilds every view and would silently throw
   *   away exclusions and primary choices the reader has made on OTHER cards
   *   and not yet saved. Telling them and letting them choose is the only
   *   option here that cannot lose work.
   * - `UNKNOWN_DECISION` can only be our own bug, because the only values
   *   this component sends are `ACCEPTED`, `REJECTED` and `EDITED`. There is
   *   nothing to explain to a user, so it reports as a plain failure and goes
   *   to the console for us.
   */
  private reportDecisionRefused(reason: ProjectMappingDecideRejectedReason): void {
    if ( reason === 'UNKNOWN_PROPOSAL' ) {
      this.serverErrorToastService.reportMessage(
        LanguagePipe.get('This group is no longer part of the current plan. Refresh the page to see the current groupings.'));
      return;
    }
    console.error('ProjectMappingReviewComponent: decision refused by the server', reason);
    this.serverErrorToastService.reportMessage(
      LanguagePipe.get('Your answer could not be saved. Nothing has been changed.'));
  }

  get answeredCount(): number {
    return this.proposalViews.filter(v => v.answered).length;
  }

  /** Batch size, for the "show N more" button. */
  get revealBatchSize(): number {
    return ProjectMappingReviewComponent.REVEAL_BATCH;
  }

  /**
   * Decide what to render, and count what is outstanding.
   *
   * Called after a load and after anything that changes a proposal's status.
   * Everything the page SUMMARISES - the status strip, the outstanding count,
   * the apply bar - keeps reading `proposalViews`, the full list. Only the
   * rendered list is trimmed, so hiding a card never changes a number.
   */
  private refreshVisible(): void {
    const all = this.proposalViews;
    this.outstandingCount = all.filter(v => v.needsDecision && !v.answered && !v.applied).length;

    const result = trimProposals<MappingProposalView>({
      all: all,
      needsAttention: v => v.needsDecision && !v.answered,
      sticky: this.sticky,
      revealed: this.revealed,
      threshold: ProjectMappingReviewComponent.LARGE_REVIEW,
      batch: ProjectMappingReviewComponent.REVEAL_BATCH
    });

    this.visibleProposals = result.visible;
    this.hiddenProposalCount = result.hidden;
    this.largeReview = result.trimmed;
    this.revealed = result.revealed;

    // A Set, not `indexOf` per item: this runs on every refresh and the list
    // reaches 1,768 on the largest organization.
    const shown = new Set<MappingProposalView>(result.visible);
    this.hiddenAlternativeCount = all.filter(v => v.hasAlternatives && !shown.has(v)).length;
  }

  /**
   * Render every group that had something considered and left out.
   *
   * Pinned rather than counted into the reveal batch, so answering one does not
   * push the rest back out of view.
   */
  revealAlternatives(): void {
    for ( const view of this.proposalViews ) {
      if ( view.hasAlternatives ) {
        this.sticky.add(view);
      }
    }
    this.refreshVisible();
  }

  revealMore(): void {
    this.revealed += ProjectMappingReviewComponent.REVEAL_BATCH;
    this.refreshVisible();
  }

  /**
   * Render everything.
   *
   * Offered because "I need to find one specific book" is a real need and a
   * trimmed list cannot serve it. The count sits on the button so the cost is
   * visible before it is paid.
   */
  revealAll(): void {
    this.revealed = this.proposalViews.length;
    this.refreshVisible();
  }

  /**
   * Identity for the proposal `ngFor`.
   *
   * `proposalId` is NOT stable across a re-plan (§9), but it is stable within
   * one rendered list, which is all `trackBy` needs. Without it, revealing a
   * batch destroys and rebuilds every card already on screen instead of
   * appending to them.
   */
  trackProposal(index: number, view: MappingProposalView): number {
    return view.proposal.proposalId;
  }

  // --- apply ----------------------------------------------------------------

  /**
   * Default selector for a CUSTOMER applying after review: only what they
   * answered. The operator's buttons name their selector explicitly, because
   * "create the clear ones" and "create all of them" are genuinely different
   * actions and hiding that behind one default would be a trap.
   */
  get defaultSelector(): ProjectMappingApplySelector {
    return this.isOperator ? 'NO_DECISION_NEEDED' : 'DECIDED';
  }

  /**
   * Proposals a bulk `ALL` would create.
   *
   * Mirrors §3.5 exactly - `ALL` applies `PROPOSED` / `ACCEPTED` / `EDITED`
   * and nothing else. This list is used to build the **complement** when
   * something is held back, so getting it wrong would silently skip Projects
   * the operator asked for. It is written against the contract's own wording
   * rather than inferred from what happens to be on screen.
   */
  private applicableProposalIds(): number[] {
    return this.proposalViews
      .filter(v => v.proposal.status === 'PROPOSED'
                || v.proposal.status === 'ACCEPTED'
                || v.proposal.status === 'EDITED')
      .map(v => v.proposal.proposalId);
  }

  /**
   * Proposals the operator has set aside for this session.
   *
   * Client-side only and deliberately not persisted: holding is "not this
   * time", not a decision about the grouping. Saying "no" to a grouping is
   * what `reject` is for, and that one IS recorded.
   *
   * Cleared on every load, because `proposalId` is not stable across a re-plan
   * (§9) and carrying ids over would hold back whatever inherited the number.
   */
  private heldProposalIds = new Set<number>();

  isHeld(view: MappingProposalView): boolean {
    return this.heldProposalIds.has(view.proposal.proposalId);
  }

  toggleHold(view: MappingProposalView): void {
    const id = view.proposal.proposalId;
    if ( this.heldProposalIds.has(id) ) {
      this.heldProposalIds.delete(id);
    } else {
      this.heldProposalIds.add(id);
    }
  }

  /** Held AND still creatable - a held proposal already applied is moot. */
  get heldCount(): number {
    return this.applicableProposalIds().filter(id => this.heldProposalIds.has(id)).length;
  }

  /**
   * Create exactly one Project, now.
   *
   * Uses the `IDS` selector, which §3.5 has supported all along and which this
   * client has never called. Every proposal is revalidated at apply time, so
   * one-at-a-time is as safe as a bulk run.
   */
  createOne(view: MappingProposalView): void {
    if ( this.applying || view.saving || this.writeUnavailable ) {
      return;
    }
    // 🚨 Save first, apply second. 🚨
    //
    // Exclusions and primary choices live on the VIEW until a decision is
    // sent; the server knows nothing about them. Applying straight away would
    // create the Project exactly as originally proposed and silently discard
    // the very edits the operator made before pressing this - including a
    // member they had just removed with the minus button.
    //
    // The apply is chained off the decision's success, not fired alongside it:
    // if the answer does not land, the Project must not be created from the
    // stale grouping.
    if ( view.edited && !view.saved ) {
      this.sendDecision(view, 'EDITED',
        () => this.applySelector('IDS', [view.proposal.proposalId]));
      return;
    }
    this.applySelector('IDS', [view.proposal.proposalId]);
  }

  /**
   * May this proposal be created on its own?
   *
   * Not after it has been rejected: "create this Project" directly contradicts
   * "keep these separate", and §3.5 does not say whether `IDS` filters by
   * status - so the client refuses rather than relying on the server to.
   */
  canCreateOne(view: MappingProposalView): boolean {
    return this.isOperator
        && !view.applied
        && !view.rejected
        && view.proposal.status !== 'REJECTED'
        && view.proposal.status !== 'SUPERSEDED';
  }

  /**
   * Proposals carrying local edits nobody has saved.
   *
   * A bulk create applies what the SERVER holds, so these would go in with
   * their original grouping. Warned about rather than auto-saved: silently
   * sending N decisions the operator did not ask for is a worse surprise than
   * telling them.
   */
  get unsavedCount(): number {
    return this.proposalViews.filter(v => v.edited && !v.saved && !v.applied).length;
  }

  /**
   * Create everything except what is held back.
   *
   * Falls through to plain `ALL` when nothing is held, so the ordinary path is
   * byte-identical to what it was - an explicit id list is only ever sent when
   * the operator has actually asked for a subset.
   */
  createAllExceptHeld(): void {
    if ( this.heldCount === 0 ) {
      this.applySelector('ALL');
      return;
    }
    const ids = this.applicableProposalIds().filter(id => !this.heldProposalIds.has(id));
    if ( ids.length === 0 ) {
      return;
    }
    this.applySelector('IDS', ids);
  }

  applySelector(selector: ProjectMappingApplySelector, proposalIds?: number[]): void {
    if ( !this.review || this.applying || this.writeUnavailable ) {
      return;
    }
    this.applying = true;
    this.writeErrorDetail = '';
    const request: ProjectMappingApplyRequest = { selector: selector };
    if ( proposalIds && proposalIds.length > 0 ) {
      request.proposalIds = proposalIds;
    }
    this.projectMappingService.apply(this.review.runId, request).subscribe(
      run => {
        if ( run && run.phase ) {
          this.review.phase = run.phase;
        }
        // 🚨 202, not 200. Nothing has been built yet. 🚨
        //
        // This used to call load(), which re-read the whole §3.3 review -- an
        // expensive answer to the cheap question "has it finished?", and one
        // that returns the SAME pre-apply payload every time, so it looked
        // like a completed apply that had changed nothing. §3.6 is the
        // endpoint for this question.
        this.beginRunWait();
      },
      error => this.reportApplyFailure('projectMapping.apply', error,
        LanguagePipe.get('The mapping could not be applied. Nothing has been created.'))
    );
  }

  /**
   * Wait for an enqueued apply or revert to finish.
   *
   * `PROJECT_MAPPING_RUN` is the fast path and the normal one. This is the
   * fallback, and it is not hypothetical: per `Notifications.md` §7 a queue is
   * garbage collected after 15 minutes, so a session that was not listening
   * misses the burst entirely -- and the screen would then sit on a disabled
   * button and a spinner with nothing ever arriving to clear it.
   */
  private beginRunWait(): void {
    this.runWaitTimedOut = false;
    this.runPollsLeft = ProjectMappingReviewComponent.RUN_POLL_MAX;
    this.startRunPoll();
  }

  private startRunPoll(): void {
    if ( this.runPollTimer || !this.review || !this.review.runId ) {
      return;
    }
    if ( this.runPollsLeft <= 0 ) {
      // Stop, and SAY we have stopped. A spinner that will never resolve is a
      // statement the screen can see is false, and it leaves the operator
      // waiting rather than refreshing.
      this.applying = false;
      this.runWaitTimedOut = true;
      return;
    }
    const runId = this.review.runId;
    this.runPollTimer = setTimeout(() => {
      this.runPollTimer = null;
      this.runPollsLeft--;
      this.projectMappingService.getRun(runId).subscribe(
        run => {
          // The screen may have moved on to another organization, or been
          // re-planned into a different run, while this was in flight.
          if ( !this.review || this.review.runId !== runId ) {
            return;
          }
          if ( run && run.phase ) {
            this.review.phase = run.phase;
          }
          if ( run && PROJECT_MAPPING_SETTLED_PHASES.indexOf(run.phase) >= 0 ) {
            this.stopRunPoll();
            this.applying = false;
            // NOW re-read the review: the proposals, counts and statuses have
            // actually changed, which is the one moment the full payload is
            // worth fetching.
            this.load();
            return;
          }
          this.startRunPoll();
        },
        error => {
          // One failed poll is not a failed apply. The run is being done by a
          // worker and is unaffected by our ability to ask about it, so keep
          // asking until the budget runs out.
          console.error('ProjectMappingReviewComponent: run poll failed', error);
          this.startRunPoll();
        }
      );
    }, ProjectMappingReviewComponent.RUN_POLL_MS);
  }

  private stopRunPoll(): void {
    if ( this.runPollTimer ) {
      clearTimeout(this.runPollTimer);
      this.runPollTimer = null;
    }
    this.runPollsLeft = 0;
    this.runWaitTimedOut = false;
  }

  /**
   * Report a failed apply or revert.
   *
   * `409 PROJECT_MAPPING_APPLY_IN_PROGRESS` is NOT a failure. The server
   * claims the run before enqueueing precisely so a second press cannot race
   * the first on the same Books, so this answer means "the thing you asked
   * for is already happening" -- the correct response is to show the wait,
   * not a red toast saying nothing was created when something is being
   * created right now. It is reachable without a double-click: the operator
   * who applied may not be the one looking at this tab.
   */
  private reportApplyFailure(operation: string, error: any, fallback: string): void {
    if ( serverErrorCode(error) === 'PROJECT_MAPPING_APPLY_IN_PROGRESS' ) {
      this.applying = true;
      this.beginRunWait();
      return;
    }
    this.applying = false;
    this.reportWriteFailure(operation, error, fallback);
  }

  /** Re-check after the screen gave up waiting on a run. */
  retryRunWait(): void {
    if ( !this.review || !this.review.runId ) {
      return;
    }
    this.applying = true;
    this.beginRunWait();
  }

  /**
   * Start a plan for this organization.
   *
   * The server answers 202 and the plan does not exist yet, so this must NOT
   * re-read the review -- doing that was the old behaviour and it simply
   * fetched the same 404 the operator had just pressed the button to get out
   * of, making a working button look like a broken one. The screen moves to
   * "being built" and waits for the run message, with a bounded poll behind
   * it.
   */
  plan(): void {
    if ( !this.canPlan ) {
      return;
    }
    this.loading = true;
    this.projectMappingService.plan(this.orgId).subscribe(
      () => {
        this.loading = false;
        this.review = null;
        this.proposalViews = [];
        this.loadFailed = false;
        this.loadErrorDetail = '';
        this.notReady = 'PLANNING';
        this.stopPlanPoll();
        this.startPlanPoll();
      },
      error => {
        this.loading = false;
        this.serverErrorToastService.reportLocalizedError('projectMapping.plan', error,
          LanguagePipe.get('The mapping plan could not be started. Nothing has been changed.'));
      }
    );
  }

  revert(): void {
    if ( !this.canRevert || this.writeUnavailable ) {
      return;
    }
    this.applying = true;
    this.writeErrorDetail = '';
    this.projectMappingService.revert(this.review.runId).subscribe(
      run => {
        // 202 as well, and onto the SAME queue as apply - which is what makes
        // the two unable to run concurrently against one organization.
        if ( run && run.phase ) {
          this.review.phase = run.phase;
        }
        this.beginRunWait();
      },
      error => this.reportApplyFailure('projectMapping.revert', error,
        LanguagePipe.get('The run could not be reverted. Nothing has been changed.'))
    );
  }

  backToDashboard(): void {
    this.router.navigate(['/secure/projectmapping']);
  }

  /**
   * Apply and revert ride the same queue and share one waiting panel, so the
   * panel has to say which of the two it is waiting for. Read off the phase
   * rather than remembering which button was pressed, because the operator
   * watching may not be the one who pressed it.
   */
  get isReverting(): boolean {
    return !!this.review && this.review.phase === 'REVERTING';
  }

  get hasRun(): boolean {
    return !!this.review && !!this.review.runId;
  }
}
